import { useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    CardPointeIpgSystemId,
    CardPointeIpgSystemIdKey,
    CardPointeGatewayEnvironment,
    CardDetailsModel,
    defaultDemoIpgConfigByKey,
    defaultIpgConfigByKey,
    type CardPointeIpgConfig,
    type IpgConfig,
    type MerchantConfig,
    PaymentActionAvailabilityStatus,
    type PaymentActionAvailability,
    type PaymentActionReadinessResponse,
    type TerminalDeviceListItem,
    CredentialType,
    resolveDefaultCardDetailsModel,
    resolveCardPointeIpgConfigFromPreset,
} from '@pillar-portal/api-types';
import { SecretAssociationCard } from '@/components/payments-infrastructure/SecretAssociationCard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { OmnisearchSelect } from '@/components/ui/omnisearch-select';
import { paymentsApi, paymentsInfrastructureApi } from '@/lib/pillarApi';
import {
    formatIpgPaymentFeatureCapabilityStateLabel,
    IpgPaymentFeatureCapabilityState,
    type IpgConfigEditorAdapter,
    type IpgPaymentFeatureCapabilityDisplayRule,
} from '../../ipg-config-editor-adapter';
import { CardDetailsModelControl } from '../CardDetailsModelControl';

export type CardPointeOrganizationIpgEnvironment = 'PRODUCTION' | 'DEMO';

export interface CardPointeOrganizationIpgConfigEditorState {
    readonly ipgSystemId: typeof CardPointeIpgSystemId;
    readonly ipgSystemIdKey: typeof CardPointeIpgSystemIdKey;
    readonly environment: CardPointeOrganizationIpgEnvironment;
    readonly gatewayEnvironment: CardPointeGatewayEnvironment;
    readonly portalUrl: string;
    readonly gatewayCredentialUuid?: string | null;
    readonly cardDetailsModel: CardDetailsModel;
    readonly includeAvs: boolean;
}

export interface CardPointeOrganizationIpgConfigEditorProps {
    readonly editorState: CardPointeOrganizationIpgConfigEditorState;
    readonly onChange: (nextState: CardPointeOrganizationIpgConfigEditorState) => void;
    readonly onPresetConfigChange: (ipgConfig: IpgConfig) => void;
    readonly productionPresetAvailable: boolean;
    readonly organizationUuid: string;
}

export interface CardPointeOrganizationIpgReadOnlySummaryProps {
    readonly editorState: CardPointeOrganizationIpgConfigEditorState;
    readonly organizationUuid: string;
}

export interface CardPointeOrganizationIpgReadinessSummaryProps {
    readonly organizationUuid: string;
}

const CARDPOINTE_READINESS_TERMINAL_PAGE_SIZE = 50;

const CardPointeReadinessActionDefinitions = [
    { key: 'cardEntry', label: 'Card entry' },
    { key: 'storedCard', label: 'Stored card' },
    { key: 'terminalSale', label: 'Terminal sale' },
] as const satisfies ReadonlyArray<{
    readonly key: keyof PaymentActionReadinessResponse;
    readonly label: string;
}>;

export const CardPointeOrganizationIpgCapabilityDisplayRules: readonly IpgPaymentFeatureCapabilityDisplayRule[] =
    [
        {
            featureId: 'browser_tokenization',
            label: 'Browser tokenization',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip:
                'CardSecure browser tokenization can create payment tokens without storing PAN in the app.',
        },
        {
            featureId: 'hosted_tokenization',
            label: 'Hosted tokenization',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'CardPointe Hosted iFrame tokenization is available.',
        },
        {
            featureId: 'terminal_payments',
            label: 'Terminal payments',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'CardPointe terminal payment APIs are configured.',
        },
        {
            featureId: 'terminal_tokenization',
            label: 'Terminal tokenization',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'CardPointe can tokenize cards from a terminal for stored-card payments.',
        },
        {
            featureId: 'heartbeat',
            label: 'Heartbeat',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'CardPointe merchant inquiry can verify gateway readiness.',
        },
        {
            featureId: 'refunds',
            label: 'Refunds',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'CardPointe refund APIs are configured.',
        },
        {
            featureId: 'voids',
            label: 'Voids',
            state: IpgPaymentFeatureCapabilityState.SUPPORTED,
            tooltip: 'CardPointe void APIs are configured.',
        },
        {
            featureId: 'ach',
            label: 'ACH payments',
            state: IpgPaymentFeatureCapabilityState.DEFERRED,
            tooltip:
                'ACH remains disabled until the CardPointe ACH guide is implemented and QA is complete.',
        },
    ];

function cloneCardPointeOrganizationIpgConfig(config: CardPointeIpgConfig): CardPointeIpgConfig {
    return {
        ...config,
        allowedWebsites: config.allowedWebsites.map((allowedWebsite) => ({
            ...allowedWebsite,
            allowedUrlPatterns: [...allowedWebsite.allowedUrlPatterns],
            urlPatternsRules: allowedWebsite.urlPatternsRules.map((fetchPolicy) => ({
                action: fetchPolicy.action,
                urlPatterns: [...fetchPolicy.urlPatterns],
            })),
            iframeUrlPatterns: allowedWebsite.iframeUrlPatterns.map((iframePolicy) => ({
                action: iframePolicy.action,
                urlPatterns: [...iframePolicy.urlPatterns],
            })),
        })),
        rpaModules: [...config.rpaModules],
    };
}

function getCardPointeOrganizationIpgPortalUrl(config: CardPointeIpgConfig): string {
    return config.allowedWebsites[0]?.url ?? '';
}

function getProductionCardPointeOrganizationIpgConfig(): CardPointeIpgConfig {
    const config = defaultIpgConfigByKey[CardPointeIpgSystemIdKey];
    if (config.ipg_id !== CardPointeIpgSystemId) {
        throw new Error('Invalid CardPointe production IPG configuration.');
    }
    return config;
}

function getDemoCardPointeOrganizationIpgConfig(): CardPointeIpgConfig {
    const demoIpgConfig = defaultDemoIpgConfigByKey[CardPointeIpgSystemIdKey];
    if (demoIpgConfig === undefined || demoIpgConfig.ipg_id !== CardPointeIpgSystemId) {
        throw new Error('Missing CardPointe demo IPG configuration.');
    }
    return demoIpgConfig;
}

export function resolveCardPointeOrganizationIpgConfig(
    merchantConfig: MerchantConfig | undefined
): CardPointeIpgConfig {
    const persistedCardPointeConfig = merchantConfig?.ipg[CardPointeIpgSystemIdKey];
    if (persistedCardPointeConfig?.ipg_id === CardPointeIpgSystemId) {
        const presetConfig =
            persistedCardPointeConfig.environment === CardPointeGatewayEnvironment.PRODUCTION
                ? getProductionCardPointeOrganizationIpgConfig()
                : getDemoCardPointeOrganizationIpgConfig();
        return resolveCardPointeIpgConfigFromPreset(presetConfig, persistedCardPointeConfig);
    }
    return resolveCardPointeIpgConfigFromPreset(getDemoCardPointeOrganizationIpgConfig());
}

function deriveCardPointeOrganizationIpgEnvironment(
    config: CardPointeIpgConfig
): CardPointeOrganizationIpgEnvironment {
    return config.environment === CardPointeGatewayEnvironment.PRODUCTION ? 'PRODUCTION' : 'DEMO';
}

export function buildCardPointeOrganizationIpgConfigEditorState(
    merchantConfig: MerchantConfig | undefined
): CardPointeOrganizationIpgConfigEditorState {
    const effectiveConfig = resolveCardPointeOrganizationIpgConfig(merchantConfig);
    const environment = deriveCardPointeOrganizationIpgEnvironment(effectiveConfig);
    return {
        ipgSystemId: CardPointeIpgSystemId,
        ipgSystemIdKey: CardPointeIpgSystemIdKey,
        environment,
        gatewayEnvironment: effectiveConfig.environment,
        portalUrl: getCardPointeOrganizationIpgPortalUrl(effectiveConfig),
        cardDetailsModel:
            effectiveConfig.card_details_model ??
            resolveDefaultCardDetailsModel(CardPointeIpgSystemId),
        includeAvs: effectiveConfig.terminal_include_avs,
        ...(effectiveConfig.gateway_credential_uuid === undefined
            ? {}
            : { gatewayCredentialUuid: effectiveConfig.gateway_credential_uuid }),
    };
}

function formatCardPointeOrganizationIpgEnvironmentLabel(
    environment: CardPointeOrganizationIpgEnvironment
): string {
    if (environment === 'PRODUCTION') {
        return 'Production';
    }
    return 'Demo';
}

export function buildCardPointeOrganizationIpgConfigForSaveOrThrow(
    editorState: CardPointeOrganizationIpgConfigEditorState
): CardPointeIpgConfig {
    const presetConfig = cloneCardPointeOrganizationIpgConfig(
        editorState.environment === 'PRODUCTION'
            ? getProductionCardPointeOrganizationIpgConfig()
            : getDemoCardPointeOrganizationIpgConfig()
    );
    return {
        ...presetConfig,
        card_details_model: editorState.cardDetailsModel,
        terminal_include_avs: editorState.includeAvs,
        ...(editorState.gatewayCredentialUuid === undefined ||
        editorState.gatewayCredentialUuid === null
            ? {}
            : { gateway_credential_uuid: editorState.gatewayCredentialUuid }),
    };
}

function CardPointeOrganizationIpgEnvironmentRadio(props: {
    readonly label: string;
    readonly checked: boolean;
    readonly onChange: () => void;
    readonly disabled: boolean;
}): ReactElement {
    return (
        <div className="flex items-center gap-2 text-sm">
            <span
                className="-m-[3px] inline-flex p-[3px]"
                onClick={(event) => {
                    if (props.disabled) {
                        return;
                    }
                    if (event.target !== event.currentTarget) {
                        return;
                    }
                    props.onChange();
                }}
            >
                <input
                    type="radio"
                    aria-label={props.label}
                    name="cardpointe_ipg_environment"
                    checked={props.checked}
                    disabled={props.disabled}
                    title={
                        props.disabled
                            ? 'Production presets are available only in production Portal environments.'
                            : undefined
                    }
                    className="h-4 w-4 accent-blue-600 disabled:opacity-50"
                    onChange={props.onChange}
                />
            </span>
            <span>{props.label}</span>
        </div>
    );
}

function buildCardPointeOrganizationIpgPresetState(
    editorState: CardPointeOrganizationIpgConfigEditorState,
    environment: CardPointeOrganizationIpgEnvironment
): CardPointeOrganizationIpgConfigEditorState {
    const presetConfig =
        environment === 'DEMO'
            ? getDemoCardPointeOrganizationIpgConfig()
            : getProductionCardPointeOrganizationIpgConfig();
    return {
        ...editorState,
        environment,
        gatewayEnvironment: presetConfig.environment,
        portalUrl: getCardPointeOrganizationIpgPortalUrl(presetConfig),
    };
}

export function CardPointeOrganizationIpgConfigEditor({
    editorState,
    onChange,
    onPresetConfigChange,
    productionPresetAvailable,
    organizationUuid,
}: CardPointeOrganizationIpgConfigEditorProps): ReactElement {
    return (
        <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">CardPointe gateway configuration</p>
            <CardDetailsModelControl
                ipgSystemId={editorState.ipgSystemId}
                value={editorState.cardDetailsModel}
                onChange={(cardDetailsModel) => onChange({ ...editorState, cardDetailsModel })}
            />
            <div className="space-y-2">
                <p className="text-sm font-medium">Environment</p>
                <div className="flex flex-wrap gap-4">
                    {(['PRODUCTION', 'DEMO'] as const).map((environment) => (
                        <CardPointeOrganizationIpgEnvironmentRadio
                            key={environment}
                            label={formatCardPointeOrganizationIpgEnvironmentLabel(environment)}
                            checked={editorState.environment === environment}
                            disabled={environment === 'PRODUCTION' && !productionPresetAvailable}
                            onChange={() => {
                                const nextState = buildCardPointeOrganizationIpgPresetState(
                                    editorState,
                                    environment
                                );
                                onChange(nextState);
                                onPresetConfigChange(
                                    buildCardPointeOrganizationIpgConfigForSaveOrThrow(nextState)
                                );
                            }}
                        />
                    ))}
                </div>
            </div>
            <p className="text-sm">Portal URL: {editorState.portalUrl}</p>
            <label className="flex items-center gap-2 text-sm">
                <Checkbox
                    aria-label="Request terminal address verification"
                    checked={editorState.includeAvs}
                    onCheckedChange={(includeAvs) => onChange({ ...editorState, includeAvs })}
                />
                Request address verification on terminal payments
            </label>
            <SecretAssociationCard
                cardTitle="Gateway credentials"
                entityLabelForDialog="CardPointe gateway"
                organizationUuid={organizationUuid}
                organizationName="Organization"
                requiredCredentialType={CredentialType.VIRTUAL_TERMINAL}
                requiredCredentialSystemId={CardPointeIpgSystemId}
                attachedSecretIdentity={
                    editorState.gatewayCredentialUuid == null
                        ? null
                        : { credential_uuid: editorState.gatewayCredentialUuid }
                }
                canEdit={true}
                onAttachSecret={(secret) =>
                    onChange({
                        ...editorState,
                        gatewayCredentialUuid: secret.credential_uuid,
                    })
                }
                onDetachSecret={() =>
                    onChange({
                        ...editorState,
                        gatewayCredentialUuid: null,
                    })
                }
                isMutating={false}
            />
        </div>
    );
}

export function CardPointeOrganizationIpgReadOnlySummary({
    editorState,
    organizationUuid,
}: CardPointeOrganizationIpgReadOnlySummaryProps): ReactElement {
    return (
        <div className="space-y-2">
            <p className="text-sm font-medium">CardPointe gateway configuration</p>
            <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                <p>
                    Environment:{' '}
                    {formatCardPointeOrganizationIpgEnvironmentLabel(editorState.environment)}
                </p>
                <p>Portal URL: {editorState.portalUrl}</p>
                <p>
                    Terminal address verification: {editorState.includeAvs ? 'Enabled' : 'Disabled'}
                </p>
                {CardPointeOrganizationIpgCapabilityDisplayRules.map((rule) => {
                    const isSupported = rule.state === IpgPaymentFeatureCapabilityState.SUPPORTED;
                    return (
                        <p key={rule.featureId}>
                            {rule.label}:{' '}
                            <span
                                title={rule.tooltip}
                                aria-disabled={isSupported ? undefined : true}
                                className={isSupported ? undefined : 'opacity-50'}
                            >
                                {formatIpgPaymentFeatureCapabilityStateLabel(rule.state)}
                            </span>
                        </p>
                    );
                })}
            </div>
            <SecretAssociationCard
                cardTitle="Gateway credentials"
                entityLabelForDialog="CardPointe gateway"
                organizationUuid={organizationUuid}
                organizationName="Organization"
                requiredCredentialType={CredentialType.VIRTUAL_TERMINAL}
                requiredCredentialSystemId={CardPointeIpgSystemId}
                attachedSecretIdentity={
                    editorState.gatewayCredentialUuid == null
                        ? null
                        : { credential_uuid: editorState.gatewayCredentialUuid }
                }
                canEdit={false}
                onAttachSecret={() => undefined}
                onDetachSecret={() => undefined}
                isMutating={false}
            />
        </div>
    );
}

function getCardPointeTerminalDeviceDisplayLabel(terminalDevice: TerminalDeviceListItem): string {
    const configuredName = terminalDevice.name?.trim();
    return configuredName !== undefined && configuredName.length > 0
        ? configuredName
        : [
              terminalDevice.processor_name,
              terminalDevice.processor_mid,
              terminalDevice.processor_tid,
              terminalDevice.processor_device_id,
          ].join(' / ');
}

function CardPointeReadinessActionSummary(props: {
    readonly action: PaymentActionAvailability;
    readonly label: string;
}): ReactElement {
    const available = props.action.status === PaymentActionAvailabilityStatus.AVAILABLE;
    return (
        <div className="rounded border p-2" data-readiness-action={props.label}>
            <p className="text-sm font-medium">{props.label}</p>
            <p className="text-sm">{available ? 'Ready' : 'Needs attention'}</p>
            {!available && <p className="text-sm text-muted-foreground">{props.action.message}</p>}
        </div>
    );
}

export function CardPointeOrganizationIpgReadinessSummary({
    organizationUuid,
}: CardPointeOrganizationIpgReadinessSummaryProps): ReactElement {
    const [selectedTerminalDevice, setSelectedTerminalDevice] =
        useState<TerminalDeviceListItem | null>(null);
    const selectedTerminalDeviceUuid = selectedTerminalDevice?.terminal_device_uuid ?? null;
    const readinessQuery = useQuery<PaymentActionReadinessResponse>({
        queryKey: [
            'payments',
            'cardpointeActionReadiness',
            organizationUuid,
            selectedTerminalDeviceUuid ?? 'none',
        ],
        queryFn: async () => {
            if (selectedTerminalDeviceUuid === null) {
                throw new Error('A terminal device must be selected for CardPointe readiness.');
            }
            return await paymentsApi.getPaymentActionReadiness(selectedTerminalDeviceUuid);
        },
        enabled: selectedTerminalDeviceUuid !== null,
        retry: false,
    });

    return (
        <div className="space-y-3 rounded-md border p-3">
            <p className="text-sm font-medium">CardPointe readiness</p>
            <OmnisearchSelect<TerminalDeviceListItem, string>
                label="Terminal device"
                placeholder="Search terminal devices..."
                pageSize={CARDPOINTE_READINESS_TERMINAL_PAGE_SIZE}
                resetToken={organizationUuid}
                overlayMenu={true}
                selectedItemId={selectedTerminalDeviceUuid}
                selectedItemLabel={
                    selectedTerminalDevice === null
                        ? undefined
                        : getCardPointeTerminalDeviceDisplayLabel(selectedTerminalDevice)
                }
                fetchPage={async (params) => {
                    const response = await paymentsInfrastructureApi.searchTerminalDevices({
                        organization_uuid: organizationUuid,
                        include_descendants: false,
                        ipg_system_id: CardPointeIpgSystemId,
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: Math.min(params.limit, CARDPOINTE_READINESS_TERMINAL_PAGE_SIZE),
                        cursor: params.cursor,
                    });
                    return {
                        items: response.terminal_devices,
                        pagination: response.pagination,
                    };
                }}
                getItemId={(terminalDevice) => terminalDevice.terminal_device_uuid}
                getItemLabel={getCardPointeTerminalDeviceDisplayLabel}
                getItemFilterText={(terminalDevice) =>
                    [
                        getCardPointeTerminalDeviceDisplayLabel(terminalDevice),
                        terminalDevice.terminal_device_uuid,
                    ]
                        .join(' ')
                        .toLowerCase()
                }
                onSelect={setSelectedTerminalDevice}
                onClearSelection={() => setSelectedTerminalDevice(null)}
            />

            {selectedTerminalDeviceUuid === null ? (
                <p className="text-sm text-muted-foreground">
                    Select a terminal device to check CardPointe readiness.
                </p>
            ) : readinessQuery.isPending ? (
                <p className="text-sm" role="status">
                    Checking CardPointe readiness...
                </p>
            ) : readinessQuery.isError ? (
                <Alert variant="destructive">
                    <AlertDescription>
                        <p>Unable to check CardPointe readiness.</p>
                        <Button
                            type="button"
                            variant="outline"
                            className="mt-2"
                            onClick={() => void readinessQuery.refetch()}
                        >
                            Retry readiness check
                        </Button>
                    </AlertDescription>
                </Alert>
            ) : (
                <div className="space-y-3">
                    {readinessQuery.isFetching && (
                        <p className="text-sm" role="status">
                            Refreshing CardPointe readiness...
                        </p>
                    )}
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                        {CardPointeReadinessActionDefinitions.map((definition) => (
                            <CardPointeReadinessActionSummary
                                key={definition.key}
                                label={definition.label}
                                action={readinessQuery.data[definition.key]}
                            />
                        ))}
                    </div>
                    <Button
                        type="button"
                        variant="outline"
                        disabled={readinessQuery.isFetching}
                        onClick={() => void readinessQuery.refetch()}
                    >
                        Retry readiness check
                    </Button>
                </div>
            )}
        </div>
    );
}

export const CardPointeOrganizationIpgConfigEditorAdapter: IpgConfigEditorAdapter<
    CardPointeOrganizationIpgConfigEditorState,
    CardPointeIpgConfig
> = {
    ipgSystemId: CardPointeIpgSystemId,
    ipgSystemIdKey: CardPointeIpgSystemIdKey,
    label: 'CardPointe',
    capabilities: {
        supportsEditableConfig: true,
        supportsReadOnlySummary: true,
        supportsDefaultCredentialPlaceholders: true,
    },
    createEditableState: ({ merchantConfig }) =>
        buildCardPointeOrganizationIpgConfigEditorState(merchantConfig),
    validateEditableState: ({ editorState }) => {
        buildCardPointeOrganizationIpgConfigForSaveOrThrow(editorState);
        return [];
    },
    buildSavePayload: ({ editorState }) =>
        buildCardPointeOrganizationIpgConfigForSaveOrThrow(editorState),
    renderEditable: ({ organizationUuid, editorState, onChange, productionPresetAvailable }) => (
        <CardPointeOrganizationIpgConfigEditor
            editorState={editorState}
            onChange={onChange}
            onPresetConfigChange={() => undefined}
            productionPresetAvailable={productionPresetAvailable}
            organizationUuid={organizationUuid}
        />
    ),
    renderReadOnlySummary: ({
        merchantConfig,
        organizationUuid,
        providerAdministrationEnabled,
    }) => (
        <div className="space-y-2">
            <CardPointeOrganizationIpgReadOnlySummary
                organizationUuid={organizationUuid}
                editorState={buildCardPointeOrganizationIpgConfigEditorState(merchantConfig)}
            />
            {providerAdministrationEnabled && (
                <CardPointeOrganizationIpgReadinessSummary organizationUuid={organizationUuid} />
            )}
        </div>
    ),
};
